import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getEvents } from '../utils/api';

function daysAway(dateStr) {
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(dateStr + 'T00:00:00');
  return Math.round((d - today) / 86400000);
}

function formatDateRange(start, end) {
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  const opts = { month: 'short', day: 'numeric' };
  if (start === end) return s.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  if (s.getFullYear() === e.getFullYear()) {
    return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
  }
  return `${s.toLocaleDateString('en-US', { ...opts, year: 'numeric' })} – ${e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

function AttendeeAvatars({ attendees }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {attendees.slice(0, 5).map(a => (
        <div key={a.id} title={a.display_name} style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'var(--color-btn)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.7rem', color: 'var(--color-text-secondary)',
          border: '2px solid var(--color-surface)',
        }}>
          {a.display_name.charAt(0)}
        </div>
      ))}
      {attendees.length > 5 && (
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--color-hover)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.65rem', color: 'var(--color-text-muted)' }}>
          +{attendees.length - 5}
        </div>
      )}
    </div>
  );
}

export default function EventsListPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getEvents()
      .then(r => setEvents(r.events || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page-loading">Loading events…</div>;
  if (error) return <div className="page-error">{error}</div>;

  const today = new Date(); today.setHours(0,0,0,0);
  const upcoming = events.filter(e => new Date(e.end_date + 'T00:00:00') >= today);
  const past = events.filter(e => new Date(e.end_date + 'T00:00:00') < today);
  const hero = upcoming[0] || null;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Networking Events</h1>
          <p className="page-subtitle">{upcoming.length} upcoming · {past.length} past</p>
        </div>
        <Link to="/events/new" className="btn btn-primary">+ New Event</Link>
      </div>

      {/* Hero card — next upcoming event */}
      {hero && (
        <Link to={`/events/${hero.id}`} style={{ textDecoration: 'none' }}>
          <div className="event-hero-card">
            <div className="event-hero-meta">
              <span className="days-away-badge">
                {daysAway(hero.start_date) === 0 ? 'Today' :
                 daysAway(hero.start_date) === 1 ? 'Tomorrow' :
                 `${daysAway(hero.start_date)} days away`}
              </span>
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>Next Up</span>
            </div>
            <h2 className="event-hero-name">{hero.name}</h2>
            <div className="event-hero-details">
              <span>{formatDateRange(hero.start_date, hero.end_date)}</span>
              {hero.venue && <span>· {hero.venue}</span>}
              {hero.city && <span>· {hero.city}{hero.state ? `, ${hero.state}` : ''}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12 }}>
              <AttendeeAvatars attendees={hero.attendees || []} />
              {parseInt(hero.contact_count) > 0 && (
                <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                  {hero.contact_count} contact{hero.contact_count !== '1' ? 's' : ''} linked
                </span>
              )}
            </div>
          </div>
        </Link>
      )}

      {/* Upcoming list */}
      {upcoming.length > 1 && (
        <div className="section">
          <h3 className="section-label">Upcoming</h3>
          <div className="card">
            {upcoming.slice(1).map((ev, i) => (
              <React.Fragment key={ev.id}>
                <Link to={`/events/${ev.id}`} className="event-row">
                  <div className="event-row-main">
                    <div className="event-row-name">{ev.name}</div>
                    <div className="event-row-meta">
                      {formatDateRange(ev.start_date, ev.end_date)}
                      {ev.city && ` · ${ev.city}${ev.state ? `, ${ev.state}` : ''}`}
                      {ev.venue && ` · ${ev.venue}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <AttendeeAvatars attendees={ev.attendees || []} />
                    {parseInt(ev.contact_count) > 0 && (
                      <span className="badge badge-neutral">{ev.contact_count} contacts</span>
                    )}
                    <span className="days-away-sm">
                      {daysAway(ev.start_date) <= 0 ? 'Active' : `${daysAway(ev.start_date)}d`}
                    </span>
                  </div>
                </Link>
                {i < upcoming.length - 2 && <div className="row-divider" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Past events */}
      {past.length > 0 && (
        <div className="section">
          <h3 className="section-label">Past</h3>
          <div className="card" style={{ opacity: 0.65 }}>
            {past.map((ev, i) => (
              <React.Fragment key={ev.id}>
                <Link to={`/events/${ev.id}`} className="event-row">
                  <div className="event-row-main">
                    <div className="event-row-name">{ev.name}</div>
                    <div className="event-row-meta">
                      {formatDateRange(ev.start_date, ev.end_date)}
                      {ev.city && ` · ${ev.city}${ev.state ? `, ${ev.state}` : ''}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <AttendeeAvatars attendees={ev.attendees || []} />
                    {parseInt(ev.contact_count) > 0 && (
                      <span className="badge badge-neutral">{ev.contact_count} contacts</span>
                    )}
                  </div>
                </Link>
                {i < past.length - 1 && <div className="row-divider" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {events.length === 0 && (
        <div className="empty-state">
          <p>No events yet.</p>
          <Link to="/events/new" className="btn btn-primary">Create your first event</Link>
        </div>
      )}
    </div>
  );
}
